/**
 * Stamps a section's <head> and its one-off setup onto every top level route the
 * section owns.
 *
 * SpaRoot reads the head off matched[0], so every top level record needs it; and
 * the setup - whatever the section has to do once, before any of its pages
 * render - has to run whichever of those routes the browser landed on, before
 * the route's component renders.
 *
 * `head` is for the sections that used to keep a MainXRouter.vue wrapper purely
 * to set a title and a favicon. The rest set theirs from their page components
 * and pass none.
 *
 * @param {object} options
 * @param {function} [options.head] a metaInfo factory from ./head
 * @param {function} [options.setup] returns a promise; run at most once
 * @param {boolean} [options.resetScroll] whether navigating inside the section
 *        scrolls back to the top - only the sections whose own router asked for
 *        it do, the rest keep the scroll position they always kept
 * @param {Array} options.routes the section's top level route records
 * @returns {Array} the same records, decorated
 */
let sectionCounter = 0;

/**
 * Which section a resolved route belongs to, or undefined for a route no section
 * owns - and for a component rendered outside the app's router altogether.
 */
export function sectionOf (route) {
    const record = route && route.matched && route.matched[0];
    return record && record.meta ? record.meta.section : undefined;
}

export default function defineSection ({head = null, setup = null, resetScroll = false, routes}) {
    // identity, not a label: it is only ever compared. One call per section, so
    // every top level route of a section shares it
    const section = `section-${sectionCounter += 1}`;
    let started = null;
    const runSetup = () => {
        if (!started) {
            started = Promise.resolve().then(setup);
        }
        return started;
    };

    return routes.map(route => {
        const meta = Object.assign({}, route.meta, {section});
        if (head) {
            meta.head = head;
        }
        if (resetScroll) {
            meta.resetScroll = true;
        }
        const decorated = Object.assign({}, route, {meta});
        if (setup && !route.redirect) {
            const own = route.beforeEnter;
            decorated.beforeEnter = (to, from, next) => {
                const proceed = () => (own ? own(to, from, next) : next());
                runSetup().then(proceed, error => {
                    // the section still renders - it just starts from an empty store,
                    // the same as it would if the request behind the setup failed
                    console.error('section setup failed', error);
                    proceed();
                });
            };
        }
        return decorated;
    });
}
